// WebGLRenderer on WebGL2: colour management, shadows, resize and dynamic resolution.
// Tone mapping happens in the post-processing chain (composer.ts), so the renderer itself does none.
import { NoToneMapping, PCFShadowMap, SRGBColorSpace, WebGLRenderer } from 'three';

export const MAX_PIXEL_RATIO = 1.5;

export function hasWebGL2(): boolean {
  try {
    const c = document.createElement('canvas');
    return !!c.getContext('webgl2');
  } catch {
    return false;
  }
}

export interface RendererHandle {
  renderer: WebGLRenderer;
  canvas: HTMLCanvasElement;
  /** CSS pixel size of the canvas. */
  size(): { width: number; height: number };
  /** Device pixel ratio after the cap and the dynamic render scale. */
  pixelRatio(): number;
  setRenderScale(scale: number): void;
  onResize(cb: (width: number, height: number, pixelRatio: number) => void): void;
}

export function createRenderer(container: HTMLElement): RendererHandle {
  const renderer = new WebGLRenderer({
    antialias: false, // SMAA in the composer
    stencil: false,
    depth: true,
    powerPreference: 'high-performance',
  });
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = NoToneMapping;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFShadowMap;
  renderer.info.autoReset = false; // the loop resets it so the debug overlay sees a whole frame
  const canvas = renderer.domElement;
  canvas.id = 'scene';
  container.appendChild(canvas);

  let scale = 1;
  const listeners: ((w: number, h: number, pr: number) => void)[] = [];
  const size = () => ({ width: container.clientWidth || innerWidth, height: container.clientHeight || innerHeight });
  const pixelRatio = () => Math.min(devicePixelRatio || 1, MAX_PIXEL_RATIO) * scale;

  const apply = () => {
    const { width, height } = size();
    const pr = pixelRatio();
    renderer.setPixelRatio(pr);
    renderer.setSize(width, height, true);
    for (const l of listeners) l(width, height, pr);
  };
  new ResizeObserver(apply).observe(container);
  apply();

  return {
    renderer, canvas, size, pixelRatio,
    setRenderScale(s) {
      if (Math.abs(s - scale) < 1e-3) return;
      scale = s;
      apply();
    },
    onResize(cb) {
      listeners.push(cb);
      const { width, height } = size();
      cb(width, height, pixelRatio());
    },
  };
}
